<template lang="pug">
div.has-fixed-footer
    .alert.alert-warning(v-if="err") {{err.message}}
    .alert.alert-success(v-if="successMessage") {{successMessage}}

    .card
        .card-header
            h5.mb-0 Biobank List
        .card-body
            .form-group
                input.form-control(type="text", v-model="searchText", placeholder="Filter by id or name")
            table.table.table-sm.table-striped.admin-biobank-table
                thead
                    tr
                        th ID
                        th Name
                        th Published
                        th Dependencies
                        th Actions
                tbody
                    tr(v-for="biobank in filteredBiobanks", :key="biobank.id")
                        td {{biobank.id}}
                        td {{biobank.name}}
                        td
                            span.badge(:class="biobank.published ? 'badge-success' : 'badge-secondary'") {{biobank.published ? "yes" : "no"}}
                        td
                            span(v-if="biobank.dependencies")
                                | users: {{biobank.dependencies.users}}, people: {{biobank.dependencies.people}}, collections: {{biobank.dependencies.collections}}
                            span(v-else) -
                        td.actions-cell
                            button.btn.btn-danger.btn-sm(
                                @click="removeBiobank(biobank)",
                                :disabled="!canDeleteBiobank(biobank)"
                            ) Delete
                            small.text-muted.ml-2(v-if="!canDeleteBiobank(biobank)") Has dependent data
                            button.btn.btn-success.btn-sm.ml-2(
                                v-if="!biobank.published",
                                @click="publishBiobank(biobank)",
                                :disabled="!canPublishBiobank(biobank)"
                            ) Publish
                            small.text-muted.ml-2.missing-publish(v-if="!biobank.published && !canPublishBiobank(biobank)")
                                | Missing: {{(biobank.missing_publish_fields || []).join(", ")}}

    .page-action-footer
        .container.page-action-footer-inner
            button.btn.btn-success(type="button", @click="openCreateDialog") Create Empty Biobank

    .modal.fade.show(v-if="showCreateDialog", tabindex="-1", role="dialog", style="display:block;background:rgba(0,0,0,0.4)")
        .modal-dialog(role="document")
            .modal-content
                .modal-header
                    h5.modal-title
                        | Create Empty Biobank
                        span.ml-2(v-if="dialogTitleId") {{dialogTitleId}}
                    button.close(type="button", aria-label="Close", @click="closeCreateDialog")
                        span(aria-hidden="true") &times;
                form(@submit.prevent="createBiobank")
                    .modal-body
                        .form-group
                            label Country
                            input.form-control(
                                type="text",
                                style="width:9ch;text-transform:uppercase",
                                placeholder="AT",
                                v-model="newBiobank.country",
                                maxlength="2",
                                required
                            )
                        .form-group
                            label Abbreviation
                            input.form-control(
                                type="text",
                                placeholder="Abbreviation",
                                v-model="newBiobank.acronym",
                                required
                            )
                        .form-group
                            label Name
                            input.form-control(type="text", placeholder="Name", v-model="newBiobank.name")
                        .form-group.mb-0
                            label Initial Allowed Domain
                            input.form-control(
                                type="text",
                                placeholder="example.org",
                                v-model="newBiobank.initial_domain",
                                required
                            )
                            small.text-muted This becomes the first allowed email domain for the new biobank.
                    .modal-footer
                        button.btn.btn-success(type="submit", :disabled="!canCreateBiobank") Create
                        button.btn.btn-secondary(type="button", @click="closeCreateDialog") Cancel
</template>

<script>
import {
    getAdminBiobanks,
    createAdminBiobank,
    setAdminBiobankPublished,
    deleteAdminBiobank,
    clearActiveBiobank
} from "../../api";

export default {
    data() {
        return {
            err: null,
            successMessage: "",
            biobanks: [],
            searchText: "",
            showCreateDialog: false,
            newBiobank: {
                country: "AT",
                acronym: "",
                name: "",
                initial_domain: ""
            }
        };
    },
    computed: {
        generatedBiobankId() {
            const country = (this.newBiobank.country || "").trim().toUpperCase();
            const acronym = (this.newBiobank.acronym || "").trim().toUpperCase();
            if (!country) return "bbmri-eric:ID:<COUNTRY>_<ABBREVIATION>";
            if (!acronym) return `bbmri-eric:ID:${country}_<ABBREVIATION>`;
            return `bbmri-eric:ID:${country}_${acronym}`;
        },
        dialogTitleId() {
            const country = (this.newBiobank.country || "").trim().toUpperCase();
            const acronym = (this.newBiobank.acronym || "").trim().toUpperCase();
            if (!acronym) return "";
            if (!country) return "";
            return `bbmri-eric:ID:${country}_${acronym}`;
        },
        canCreateBiobank() {
            const country = (this.newBiobank.country || "").trim().toUpperCase();
            const acronym = (this.newBiobank.acronym || "").trim().toUpperCase();
            const initialDomain = (this.newBiobank.initial_domain || "").trim().toLowerCase();
            if (!country || !acronym) return false;
            if (!initialDomain) return false;
            if (!/^[A-Z]{2}$/.test(country)) return false;
            if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(initialDomain)) return false;
            if (this.generatedBiobankId.includes("<ABBREVIATION>")) return false;
            return true;
        },
        filteredBiobanks() {
            const needle = (this.searchText || "").trim().toLowerCase();
            if (!needle) return this.biobanks;
            return this.biobanks.filter(biobank =>
                (biobank.id || "").toLowerCase().includes(needle) ||
                (biobank.name || "").toLowerCase().includes(needle)
            );
        }
    },
    created() {
        this.reload();
    },
    mounted() {
        window.addEventListener("keydown", this.onGlobalKeydown);
    },
    beforeDestroy() {
        window.removeEventListener("keydown", this.onGlobalKeydown);
    },
    methods: {
        onGlobalKeydown(event) {
            if (!this.showCreateDialog) return;
            if (event.key === "Escape") {
                event.preventDefault();
                this.closeCreateDialog();
            }
        },
        openCreateDialog() {
            this.showCreateDialog = true;
        },
        closeCreateDialog() {
            this.showCreateDialog = false;
            this.newBiobank = {country: "AT", acronym: "", name: "", initial_domain: ""};
        },
        reload() {
            getAdminBiobanks()
                .then(biobanks => {
                    this.err = null;
                    this.biobanks = biobanks;
                })
                .catch(err => this.err = err);
        },
        createBiobank() {
            if (!this.canCreateBiobank) return;
            const payload = {
                ...this.newBiobank,
                id: this.generatedBiobankId
            };
            createAdminBiobank(payload)
                .then(() => {
                    this.successMessage = `Biobank ${payload.id} created`;
                    this.closeCreateDialog();
                    this.reload();
                })
                .catch(err => this.err = err);
        },
        removeBiobank(biobank) {
            if (!this.canDeleteBiobank(biobank)) return;
            if (!confirm(`Do you really want to delete the ${biobank.id} biobank?`)) return;
            deleteAdminBiobank(biobank.id)
                .then(() => {
                    this.successMessage = `Biobank ${biobank.id} deleted`;
                    const currentBiobank = localStorage.getItem("biobank");
                    if (currentBiobank === biobank.id) {
                        return clearActiveBiobank()
                            .catch(() => ({success: true}))
                            .then(() => {
                                localStorage.removeItem("biobank");
                                localStorage.removeItem("allowed_domains");
                                localStorage.removeItem("domain");
                                this.$router.push("/admin/access");
                            });
                    }
                    return Promise.resolve();
                })
                .then(() => this.reload())
                .catch(err => this.err = err);
        },
        canDeleteBiobank(biobank) {
            return !!(biobank && biobank.dependencies && biobank.dependencies.can_delete);
        },
        canPublishBiobank(biobank) {
            return !!(biobank && biobank.can_publish);
        },
        publishBiobank(biobank) {
            if (!this.canPublishBiobank(biobank)) return;
            setAdminBiobankPublished(biobank.id, true)
                .then(() => {
                    this.successMessage = `Biobank ${biobank.id} published`;
                    this.reload();
                })
                .catch(err => this.err = err);
        }
    }
};
</script>

<style scoped>
.admin-biobank-table {
    table-layout: fixed;
}

.admin-biobank-table th:nth-child(1),
.admin-biobank-table td:nth-child(1) {
    width: 25%;
}

.admin-biobank-table th:nth-child(2),
.admin-biobank-table td:nth-child(2) {
    width: 17%;
}

.admin-biobank-table th:nth-child(3),
.admin-biobank-table td:nth-child(3) {
    width: 8%;
}

.admin-biobank-table th:nth-child(4),
.admin-biobank-table td:nth-child(4) {
    width: 28%;
}

.admin-biobank-table th:nth-child(5),
.admin-biobank-table td:nth-child(5) {
    width: 22%;
}

.admin-biobank-table td:nth-child(1),
.admin-biobank-table td:nth-child(2),
.admin-biobank-table td:nth-child(4) {
    overflow-wrap: anywhere;
}

.missing-publish {
    display: inline-block;
    font-size: 0.72rem;
    line-height: 1.15;
    white-space: normal;
    max-width: 11rem;
}
</style>
